import { omit } from 'lodash';
import { Global, css } from '@emotion/react';
import AutosizeInput from 'react-input-autosize';
import React, { useRef, useState, useEffect } from 'react';
import TagsInput, { ReactTagsInputProps } from 'react-tagsinput';

import colours from '~/utils/colours';
import useEventListener from '~/react-hooks/use-event-listener';

export interface TaggedInputProps extends ReactTagsInputProps {
  placeholder?: string;
  inputRef:
    | React.MutableRefObject<HTMLInputElement | undefined>
    | ((el?: HTMLInputElement) => void);
  onKeyEvent: (
    event: React.SyntheticEvent<HTMLInputElement, 'keydown'>
  ) => void;
}

export default function TaggedInput({
  placeholder = 'Add a tag',
  inputRef,
  onKeyEvent,
  onlyUnique = false,
  ...props
}: TaggedInputProps) {
  const [ref, setRef] = useState(useRef<HTMLInputElement>());

  function attachRef(el: HTMLInputElement) {
    setRef({ current: el });
  }

  useEffect(() => {
    if (typeof inputRef === 'function') {
      inputRef(ref.current);
    } else if (typeof inputRef === 'object' && inputRef.current) {
      inputRef.current = ref.current;
    }
  }, [ref]);

  useEventListener('keydown', onKeyEvent, ref);

  useEventListener('focus', (evt) => evt.currentTarget?.select(), ref);

  const renderInput: ReactTagsInputProps['renderInput'] = (props) => {
    const { inputRef, ...inputProps } = omit(props, 'addTag');

    return <AutosizeInput type='text' inputRef={inputRef} {...inputProps} />;
  };

  const inputProps = {
    className: 'react-tagsinput-input',
    placeholder,
    inputRef: attachRef,
  };

  return (
    <>
      <Global
        styles={css`
          .react-tagsinput {
            color: ${colours.grey};
            background-color: white;
            border: 2px solid ${colours.formBgGrey};
            overflow: hidden;
            padding-left: 5px;
            padding-top: 5px;
            border-radius: 4px;
            font-weight: bold;
            font-size: 1.15em;
          }

          .react-tagsinput--focused {
            border-color: ${colours.blue};
          }

          .react-tagsinput-tag {
            background-color: ${colours.formBgGrey};
            border-radius: 4px;
            border: none;
            color: ${colours.textFormControl};
            display: inline-block;
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 5px;
            margin-right: 5px;
            padding: 3px;
          }

          .react-tagsinput-remove {
            cursor: pointer;
            font-weight: bold;
            text-decoration: none;
            color: inherit;

            &:hover,
            &:focus {
              text-decoration: none;
            }
          }

          .react-tagsinput-tag a::before {
            content: ' ×';
          }

          .react-tagsinput-input {
            background: transparent;
            color: ${colours.textFormControl};
            font-weight: bold;
            font-size: 1.15em;
            margin-bottom: 6px;
            margin-top: 1px;
            outline: none;
            padding: 5px;

            input {
              border: 0;
              outline: none;
            }
          }
        `}
      />
      <TagsInput renderInput={renderInput} inputProps={inputProps} {...props} />
    </>
  );
}